import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Trophy, Medal, IndianRupee, ReceiptText, Footprints, ClipboardList, Users } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/common";
import { CollectionsReconciliation } from "../../components/CollectionsReconciliation";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { StaffAvatar, StaffLabel } from "../../components/StaffAvatar";
import { Progress } from "../../components/ui/progress";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg, fmtINR } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";

const PERIODS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "all_time", label: "All time" },
];

const RANK_STYLES = {
  1: { icon: Trophy, cls: "text-amber-500", label: "1st" },
  2: { icon: Medal, cls: "text-slate-400", label: "2nd" },
  3: { icon: Medal, cls: "text-orange-700/70", label: "3rd" },
};

const StatCard = ({ icon: Icon, label, value, testId, hint }) => (
  <Card className="shadow-sm">
    <CardContent className="p-4 flex items-center gap-3">
      <div className="h-9 w-9 rounded-md bg-accent flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-accent-foreground" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-lg font-semibold font-heading truncate" data-testid={testId}>{value}</p>
        {hint && <p className="text-[11px] text-muted-foreground tabular-nums truncate">{hint}</p>}
      </div>
    </CardContent>
  </Card>
);

/** 'YYYY-MM-DD' of the day before an exclusive end date (leaderboard periods are half-open). */
const prevDay = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};

const RankCell = ({ rank }) => {
  const style = RANK_STYLES[rank];
  if (!style) return <span className="text-sm text-muted-foreground font-medium pl-1.5">#{rank}</span>;
  const Icon = style.icon;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className={`h-4 w-4 ${style.cls}`} />
      <span className="text-sm font-semibold">{style.label}</span>
    </span>
  );
};

export default function Leaderboard() {
  const { code } = useParams();
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const live = useLiveCollections(code);   // Phase 149: rankings follow the live ledger
  useEffect(() => {
    let alive = true;
    setLoading((l) => l || !data || !live);   // live refreshes keep the current table on screen (no skeleton)
    api.get(`/t/${code}/leaderboard/collectors`, { params: { period } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, period, live]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.collectors || [];
  const totals = data?.totals || {};

  return (
    <div>
      <PageHeader title="Collector Leaderboard" subtitle="Tax collectors ranked by collections and field visits" />

      <Tabs value={period} onValueChange={(v) => v && setPeriod(v)} className="mb-4">
        <TabsList data-testid="leaderboard-period-tabs">
          {PERIODS.map((p) => (
            <TabsTrigger key={p.value} value={p.value} data-testid={`leaderboard-period-${p.value}`}>{p.label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[72px] rounded-lg" />)}
          </div>
          <Skeleton className="h-64 rounded-lg" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard icon={IndianRupee} label="Collected · all receipts" value={fmtINR(totals.all_receipts_amount ?? totals.amount_collected ?? 0)} testId="leaderboard-total-collected"
              hint={totals.all_receipts_amount != null ? `${fmtINR(totals.amount_collected || 0)} credited to collectors` : null} />
            <StatCard icon={ReceiptText} label="Receipts" value={(totals.all_receipts_count ?? totals.receipts ?? 0).toLocaleString("en-IN")} testId="leaderboard-total-receipts"
              hint={totals.all_receipts_count != null ? `${(totals.receipts || 0).toLocaleString("en-IN")} by collectors` : null} />
            <StatCard icon={Footprints} label="Field visits" value={totals.visits || 0} testId="leaderboard-total-visits" />
            <StatCard icon={ClipboardList} label="Reassessments" value={totals.reassessments || 0} testId="leaderboard-total-reassessments" />
          </div>

          {data?.others && (
            <div className="mb-4">
              <CollectionsReconciliation
                unified={{ amount: totals.all_receipts_amount, receipts: totals.all_receipts_count }}
                credited={{ amount: totals.amount_collected, receipts: totals.receipts }}
                others={data.others} rangeLabel={PERIODS.find((p) => p.value === period)?.label}
                collectionsHref={data.date_from ? `/${code}/admin/collections?date_from=${data.date_from}&date_to=${prevDay(data.date_to_exclusive)}` : `/${code}/admin/collections`}
                testId="leaderboard-reconciliation" compact />
            </div>
          )}

          <Card className="shadow-sm">
            <CardContent className="p-0">
              {rows.length === 0 ? (
                <div className="py-12 text-center text-sm text-muted-foreground" data-testid="leaderboard-empty">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No tax collectors found for this municipality yet.
                </div>
              ) : (
                <Table data-testid="leaderboard-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Rank</TableHead>
                      <TableHead>Collector</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Receipts</TableHead>
                      <TableHead className="text-right">Visits</TableHead>
                      <TableHead className="text-right">Reassessments</TableHead>
                      <TableHead className="w-44">Assignment coverage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const pct = r.assigned ? Math.round((r.assignments_collected / r.assigned) * 100) : 0;
                      return (
                        <TableRow key={r.collector_id} className={r.rank === 1 ? "bg-accent/40" : undefined}
                          data-testid={`leaderboard-row-${r.rank}`}>
                          <TableCell><RankCell rank={r.rank} /></TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StaffAvatar code={code} userId={r.collector_id} name={r.name} size="sm" testId={`leaderboard-avatar-${r.rank}`} />
                              <span className="text-sm font-medium"><StaffLabel code={code} userId={r.collector_id} name={r.name} empCode={r.employee_id} /></span>
                              {!r.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-semibold tabular-nums" data-testid={`leaderboard-amount-${r.rank}`}>
                            {fmtINR(r.amount_collected)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.receipts}</TableCell>
                          <TableCell className="text-right tabular-nums" data-testid={`leaderboard-visits-${r.rank}`}>{r.visits}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.reassessments}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={pct} className="h-2 flex-1" />
                              <span className="text-xs text-muted-foreground tabular-nums w-20 shrink-0">
                                {r.assignments_collected}/{r.assigned} ({pct}%)
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
